import {
  trace,
  Span,
  SpanStatusCode,
  context as otelContext,
  propagation,
  Attributes,
  Tracer,
} from '@opentelemetry/api'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  SimpleSpanProcessor,
} from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions'

import { getApiUrl } from '$extensionSrc/consts/urlConsts'
import { getActiveTabId } from '$extensionSrc/data/activeTabId'
import { _debug } from '$extensionSrc/utils/logging'

import { AutomationRunType, PLATFORM } from './enums'

const EVENTS_ENDPOINT = '/api/evt/'

type RunState = {
  runId: string
  machineName: string
  root: Span
  stage?: {
    stageKey: string
    span: Span
  }
}

const runsByTab = new Map<number, RunState>()

let tracerProvider: WebTracerProvider | null = null
let tracer: ReturnType<typeof trace.getTracer> | null = null
let isInitialized = false

// Cache parent context carrier for Apollo link (content script side)
// Only populated during active automation runs
let cachedParentContext: Record<string, string> | null = null

async function forceFlush(): Promise<void> {
  try {
    await tracerProvider?.forceFlush()
  } catch (err) {
    _debug(`Failed to force flush: ${err}`, 'tracing', 'e')
  }
}

/**
 * Initialize OpenTelemetry tracing
 * Should be called once at script startup
 */
export async function initTracing(
  isBackground: boolean = false,
): Promise<void> {
  if (isInitialized) {
    return
  }

  try {
    const endpointUrl = getApiUrl(EVENTS_ENDPOINT, null)
    const exporter = new OTLPTraceExporter({
      url: endpointUrl,
    })

    const spanProcessor = isBackground
      ? new BatchSpanProcessor(exporter)
      : new SimpleSpanProcessor(exporter)

    tracerProvider = new WebTracerProvider({
      resource: resourceFromAttributes({
        [ATTR_SERVICE_NAME]: 'goby-automation',
        [ATTR_SERVICE_VERSION]: chrome.runtime.getManifest().version,
      }),
      spanProcessors: [spanProcessor],
    })

    tracerProvider.register({
      contextManager: new ZoneContextManager(),
    })
    tracer = tracerProvider.getTracer('goby-automation', '1.0.0')

    if (!isBackground) {
      // Best-effort flush before page unload
      window.addEventListener('beforeunload', forceFlush)
      window.addEventListener('pagehide', forceFlush)
    }

    isInitialized = true

    _debug(
      `OpenTelemetry tracing initialized ${isBackground ? '(background)' : '(content)'}`,
      'tracing',
      'd',
    )
  } catch (error) {
    _debug(
      `Failed to initialize tracing ${isBackground ? '(background)' : '(content)'}: ${error}`,
      'tracing',
      'e',
    )
  }
}

export function getTracer(): Tracer {
  if (!tracer) {
    throw new Error('Tracing not initialized. Call initTracing() first.')
  }
  return tracer
}

/**
 * Propagate trace context from a span to a W3C carrier
 * @param span - The span to propagate context from
 * @returns The W3C carrier
 */
function propagateContext(span: Span): Record<string, string> {
  try {
    const ctx = trace.setSpan(otelContext.active(), span)
    const carrier: Record<string, string> = {}
    propagation.inject(ctx, carrier)
    return carrier
  } catch (error) {
    _debug(`Failed to propagate from span: ${error}`, 'tracing', 'e')
    return {}
  }
}

/**
 * Extract context from a W3C carrier
 * @param carrier - The W3C carrier to extract context from
 * @returns The extracted context
 */
export function extractContext(
  carrier: Record<string, string>,
): ReturnType<typeof otelContext.active> | null {
  return propagation.extract(otelContext.active(), carrier)
}

/**
 * Cache the parent context carrier for use in Apollo links (content script side)
 * Call this whenever you receive a context from the background script
 * @param carrier - The W3C carrier containing traceparent/tracestate headers
 */
export function setCachedParentContext(
  carrier: Record<string, string> | null,
): void {
  cachedParentContext = carrier
}

/**
 * Get the cached parent context carrier
 * Returns null if no context has been cached (i.e., no active automation run)
 * This is used by Apollo link to inject trace headers without messaging overhead
 * @returns The cached W3C carrier or null
 */
export function getCachedParentContext(): Record<string, string> | null {
  return cachedParentContext
}

/**
 * Clear the cached context (e.g., when run ends)
 */
export function clearCachedParentContext(): void {
  cachedParentContext = null
}

/**
 * Get parent context carrier for background script API calls
 * Validates against active tab IDs to ensure we're using the correct automation's context
 * @returns The W3C carrier for the active run/stage or null
 */
export async function getBackgroundParentContext(): Promise<Record<
  string,
  string
> | null> {
  const activeRuns = Array.from(runsByTab.entries()) // Get [tabId, state] pairs

  if (activeRuns.length === 0) {
    return null
  }

  for (const [tabId, state] of activeRuns) {
    const platform = state.machineName as PLATFORM
    if (PLATFORM[platform]) {
      try {
        const activeTabId = await getActiveTabId(platform)
        if (activeTabId === tabId) {
          const span = state.stage?.span ?? state.root
          return propagateContext(span)
        }
      } catch (err) {
        _debug(
          `Error checking active tab for ${platform}: ${err}`,
          'tracing',
          'w',
        )
      }
    }
  }

  const [, state] = activeRuns[0]
  const span = state.stage?.span ?? state.root
  return propagateContext(span)
}

/**
 * Start a new run
 * @param machineName - The name of the machine to start the run for
 * @param tabId - The ID of the tab to start the run for
 * @param runType - The type of automation run (e.g., SINGLE_GATHER, FULL_UPDATE)
 * @param isAutoscan - Whether this automation is running via autoscan
 * @returns The run ID and context
 */
export async function handleMessageStartRun(
  machineName: string,
  tabId: number,
  runType?: AutomationRunType,
  isAutoscan?: boolean,
) {
  if (!tracer) {
    return { ok: false, error: 'tracer-not-initialized' }
  }

  const existingState = runsByTab.get(tabId)
  let runId = existingState?.runId
  if (runId) {
    _debug(
      `START_MACHINE: returning existing run ${runId} for tab ${tabId}`,
      'tracing',
      'd',
    )
    return {
      ok: true,
      runId,
      ctx: propagateContext(existingState.stage?.span ?? existingState.root),
    }
  }

  runId = crypto.randomUUID()
  const attributes: Record<string, string | boolean> = {
    'automation.runId': runId,
    'automation.machineName': machineName,
    'automation.isAutoscan': isAutoscan ?? false,
  }
  if (runType) {
    attributes['automation.runType'] = runType
  }
  const root = tracer.startSpan('automationMachine', {
    attributes,
  })
  const newState: RunState = { runId, machineName, root }
  runsByTab.set(tabId, newState)

  _debug(`START_MACHINE: ${machineName}`, 'tracing', 'd')

  return {
    ok: true,
    runId,
    ctx: propagateContext(root),
  }
}

/**
 * Start a new stage (implicitly ends previous stage if present)
 * @param stageKey - The key of the stage to start
 * @param tabId - The ID of the tab to start the stage for
 * @returns The stage ID and context
 */
export async function handleMessageStartStage(stageKey: string, tabId: number) {
  if (!tracer) {
    return { ok: false, error: 'tracer-not-initialized' }
  }

  const state = runsByTab.get(tabId)

  if (!state) {
    return { ok: false, error: 'no-active-run' }
  }

  // End existing stage if present
  if (state.stage) {
    if (state.stage.stageKey !== stageKey) {
      state.stage.span.end()
    } else {
      return {
        ok: true,
        runId: state.runId,
        ctx: propagateContext(state.stage.span),
      }
    }
  }

  const ctx = trace.setSpan(otelContext.active(), state.root)
  const stage = tracer.startSpan(
    `automationStage`,
    {
      attributes: {
        'automation.stageKey': stageKey,
        'automation.machineName': state.machineName,
        'automation.runId': state.runId,
      },
    },
    ctx,
  )
  state.stage = { stageKey, span: stage }

  return {
    ok: true,
    runId: state.runId,
    ctx: propagateContext(state.stage.span),
  }
}

/**
 * End a run
 * @param tabId - The ID of the tab to end the run for
 * @returns True if the run was ended successfully, false otherwise
 */
function endRun(tabId: number): boolean {
  const state = runsByTab.get(tabId)
  if (!state) {
    return false
  }

  if (state.stage) {
    state.stage.span.end()
  }
  state.root.end()
  runsByTab.delete(tabId)
  return true
}

/**
 * End a run
 * @param tabId - The ID of the tab to end the run for
 * @returns True if the run was ended successfully, false otherwise
 */
export async function handleMessageEndRun(tabId: number) {
  const ok = endRun(tabId)
  await forceFlush()
  return { ok }
}

export function wireBackgroundResilience() {
  // Clean up when tabs are closed
  chrome.tabs.onRemoved.addListener((tabId) => {
    endRun(tabId)
    forceFlush()
  })

  // Flush on service worker suspend (MV3)
  const runtime = chrome.runtime as any
  if (runtime.onSuspend) {
    runtime.onSuspend.addListener(forceFlush)
  }
}

/**
 * Clean up content-side tracing (flush spans)
 * Call this when automation completes or page unloads
 */
export function cleanupContentTracing(): void {
  forceFlush()
}

/**
 * Record an error on a span
 * @param span - The span to record the error on
 * @param error - The error to record
 */
export function recordErrorOnSpan(span: Span, error: Error): void {
  try {
    span.recordException(error)
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: String(error?.message ?? error),
    })
  } catch (err) {
    _debug(`Failed to record error on span: ${err}`, 'tracing', 'e')
  }
}

/**
 * Create a span and execute a function within its context
 *
 * @param name - Span name
 * @param fn - Function to execute within span context
 * @param attrs - Optional span attributes
 * @param parentCtx - Optional parent context (for advanced use cases)
 * @returns Result of fn
 */
export async function withSpan<T>(
  name: string,
  fn: (span: Span) => Promise<T> | T,
  attrs?: Attributes,
  parentCtx?: ReturnType<typeof otelContext.active>,
): Promise<T> {
  const spanTracer = getTracer()

  let parent = parentCtx
  if (!parent) {
    // Check if there's an active span in current context
    const activeSpan = trace.getSpan(otelContext.active())
    if (activeSpan) {
      // We have an active span - use it for proper nesting
      parent = otelContext.active()
    } else if (cachedParentContext) {
      // No active span but we have cached automation context
      // This handles orphaned async operations (e.g., Promise.any losers)
      parent = extractContext(cachedParentContext) ?? otelContext.active()
    } else {
      // Fallback to active context (non-automation usage)
      parent = otelContext.active()
    }
  }

  // Start span with parent context
  const span = spanTracer.startSpan(name, { attributes: attrs }, parent)
  try {
    const ctx = trace.setSpan(parent, span)
    return await otelContext.with(ctx, () => fn(span))
  } catch (err: any) {
    recordErrorOnSpan(span, err)
    throw err
  } finally {
    span.end()
  }
}

/**
 * Create a span that bridges to the automation trace context
 *
 * @param name - Span name
 * @param fn - Function to execute within span context
 * @param attrs - Optional span attributes
 * @returns Result of fn
 */
export async function withAutomationStageSpan<T>(
  name: string,
  fn: (span: Span) => Promise<T> | T,
  attrs?: Attributes,
): Promise<T> {
  if (!cachedParentContext) {
    throw new Error('No cached automation context available')
  }

  const parentCtx = extractContext(cachedParentContext) ?? otelContext.active()
  return withSpan(name, fn, attrs, parentCtx)
}
